{
  /* Virtual-scroll infinite pagination.
     - Auto-loads next page when the bottom sentinel enters the viewport.
     - Caps DOM at N pages by trimming the oldest page (replaced with a sized
       spacer that preserves scroll position).
     - Re-hydrates trimmed pages when the user scrolls back up into a spacer.
     - Keeps the URL in sync with the page in the viewport center. */

  const SEL_SENTINEL_BOTTOM = '[data-infinite-sentinel-bottom]';
  const SEL_LOADING = '[data-infinite-loading]';
  const SEL_CURRENT = '[data-infinite-current]';

  /* Defensively neutralize Scroll Bee app if it's still loaded — otherwise it
     races our fetch and double-injects products into the grid. Locks the
     methods Scroll Bee would use to re-arm itself, so a single kill is enough. */
  const killScrollBee = (sb) => {
    if (!sb) return;
    const noop = () => {};
    const noopPromise = () => Promise.resolve();
    const lock = (key, value) => {
      try { Object.defineProperty(sb, key, { value, writable: false, configurable: false }); } catch (e) { /* noop */ }
    };
    lock('applyObserver', noop);
    lock('reapplyObserver', noop);
    lock('fetchNewHTML', noopPromise);
    lock('isLoading', true);
    lock('startLoading', noop);
    lock('createLoadingElement', noop);
    lock('setupExecution', noop);
    try { if (typeof sb.stopLoading === 'function') sb.stopLoading(); } catch (e) { /* noop */ }
    try { if (typeof sb.clearCurrentObserver === 'function') sb.clearCurrentObserver(); } catch (e) { /* noop */ }
    try { if (sb.config) sb.config.disableLoading = true; } catch (e) { /* noop */ }
    const el = document.getElementById('__scrollBeeLoadingElement');
    if (el) el.remove();
  };

  killScrollBee(window.ScrollBee);
  try {
    let _sb = window.ScrollBee;
    Object.defineProperty(window, 'ScrollBee', {
      configurable: true,
      get: () => _sb,
      set: (v) => { _sb = v; killScrollBee(v); },
    });
  } catch (e) { /* noop */ }

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.gridSelector = this.dataset.grid || '#CollectionLoop';
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.windowPages = parseInt(this.dataset.windowPages, 10) || 3;
      this.nextUrl = this.dataset.nextUrl || '';
      this.maxLoadedPage = this.currentPage;

      this.baseUrl = (() => {
        const u = new URL(window.location.href);
        u.searchParams.delete('page');
        return u.pathname + (u.search || '');
      })();

      this.pagesInDom = new Set([this.currentPage]);
      this.loading = false;
      this.bottomObserver = null;
      this.spacerObservers = new Map();
    }

    connectedCallback() {
      this.grid = document.querySelector(this.gridSelector);
      if (!this.grid) return;

      this.tagInitialChildren();
      this.seedPreviousSkeletons();
      this.setupBottomObserver();
      this.setupScrollUrlSync();
    }

    /* When the user lands on ?page=N directly (N > 1), there's no content
       above their current grid. Without this, scrolling up does nothing and
       URL never resets to the canonical /collection (page 1).
       Seed skeleton groups for pages 1..N-1 above the current grid so users
       see content "exists" above and the IO can rehydrate it on scroll up.
       Scroll position is preserved so user stays on their landed page. */
    seedPreviousSkeletons() {
      if (this.currentPage <= 1) return;
      const itemsPerPage = this.grid.children.length;
      if (itemsPerPage === 0) return;

      const firstReal = this.grid.firstElementChild;
      const firstRealTopBefore = firstReal.getBoundingClientRect().top + window.scrollY;

      for (let p = 1; p < this.currentPage; p++) {
        const skeletons = [];
        for (let i = 0; i < itemsPerPage; i++) {
          const skel = document.createElement('div');
          skel.className = 'grid-item infinite-scroll__skeleton';
          skel.dataset.virtualSkeletonPage = String(p);
          this.grid.insertBefore(skel, firstReal);
          skeletons.push(skel);
        }
        this.observeSkeletonGroup(skeletons, p);
      }

      /* Preserve scroll — inserted skeletons pushed firstReal down by their
         total height. Compensate so user keeps seeing the same products. */
      const firstRealTopAfter = firstReal.getBoundingClientRect().top + window.scrollY;
      const delta = firstRealTopAfter - firstRealTopBefore;
      if (delta > 0) {
        window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
      }
    }

    disconnectedCallback() {
      if (this.bottomObserver) this.bottomObserver.disconnect();
      this.spacerObservers.forEach((obs) => obs.disconnect());
      this.spacerObservers.clear();
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
      }
    }

    tagInitialChildren() {
      Array.from(this.grid.children).forEach((c) => {
        c.dataset.virtualPage = String(this.currentPage);
      });
    }

    setupBottomObserver() {
      const sentinel = this.querySelector(SEL_SENTINEL_BOTTOM);
      if (!sentinel) return;
      if (!this.nextUrl) {
        sentinel.style.display = 'none';
        return;
      }
      this.bottomObserver = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.loadNext();
        });
      }, { rootMargin: '600px 0px 600px 0px' });
      this.bottomObserver.observe(sentinel);
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;

      const targetPage = this.maxLoadedPage + 1;
      const url = this.nextUrl;

      this.loading = true;
      this.showLoading(true);

      try {
        const doc = await this.fetchDoc(url);
        const newGrid = doc.querySelector(this.gridSelector);
        if (!newGrid) throw new Error('Grid not found in response');

        const newChildren = Array.from(newGrid.children);
        newChildren.forEach((c) => { c.dataset.virtualPage = String(targetPage); });

        const frag = document.createDocumentFragment();
        newChildren.forEach((c) => frag.appendChild(c));
        this.grid.appendChild(frag);

        this.pagesInDom.add(targetPage);
        this.maxLoadedPage = targetPage;

        /* Read next-url from the fetched doc's own infinite-scroll element */
        const newInfinite = doc.querySelector('infinite-scroll');
        this.nextUrl = newInfinite ? (newInfinite.dataset.nextUrl || '') : '';

        this.trimIfNeeded('top');

        this.updateUrl(targetPage);

        if (!this.nextUrl) {
          const sentinel = this.querySelector(SEL_SENTINEL_BOTTOM);
          if (sentinel) sentinel.style.display = 'none';
        }

        this.dispatchEvent(new CustomEvent('infinite-scroll:loaded', {
          bubbles: true,
          detail: { page: targetPage, url },
        }));
      } catch (err) {
        console.error('[infinite-scroll] loadNext', err);
      } finally {
        this.loading = false;
        this.showLoading(false);
        /* IO only fires on intersection-state change. If user fast-scrolled
           past the sentinel during the fetch (common on mobile momentum scroll),
           sentinel may still be in viewport after load — IO won't re-fire.
           Explicitly re-check after layout settles. */
        requestAnimationFrame(() => {
          this.checkBottomSentinel();
          this.checkPendingSpacers();
        });
      }
    }

    checkBottomSentinel() {
      if (this.loading || !this.nextUrl) return;
      const sentinel = this.querySelector(SEL_SENTINEL_BOTTOM);
      if (!sentinel || sentinel.style.display === 'none') return;
      const rect = sentinel.getBoundingClientRect();
      /* Same rootMargin (600px) as the IntersectionObserver */
      if (rect.top < window.innerHeight + 600 && rect.bottom > -600) {
        this.loadNext();
      }
    }

    async rehydratePage(skeletonAnchor, pageNum) {
      if (this.loading) return;
      this.loading = true;
      this.showLoading(true);

      try {
        const url = this.pageUrl(pageNum);
        const doc = await this.fetchDoc(url);
        const newGrid = doc.querySelector(this.gridSelector);
        if (!newGrid) throw new Error('Grid not found on rehydrate');

        const newChildren = Array.from(newGrid.children);
        newChildren.forEach((c) => { c.dataset.virtualPage = String(pageNum); });

        /* Find all skeleton placeholders for this page (siblings in the grid) */
        const skeletons = Array.from(
          this.grid.querySelectorAll('[data-virtual-skeleton-page="' + pageNum + '"]')
        );
        if (skeletons.length === 0) throw new Error('No skeletons found for page ' + pageNum);

        const firstSkeleton = skeletons[0];
        const skeletonTopBefore = firstSkeleton.getBoundingClientRect().top + window.scrollY;

        const frag = document.createDocumentFragment();
        newChildren.forEach((c) => frag.appendChild(c));
        firstSkeleton.parentNode.insertBefore(frag, firstSkeleton);
        skeletons.forEach((s) => s.remove());

        this.pagesInDom.add(pageNum);

        /* Adjust scroll if real content height differs from skeleton estimate */
        if (newChildren.length > 0) {
          const newFirstTop = newChildren[0].getBoundingClientRect().top + window.scrollY;
          const delta = newFirstTop - skeletonTopBefore;
          if (Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
          }
        }

        this.trimIfNeeded('bottom');
      } catch (err) {
        console.error('[infinite-scroll] rehydratePage', err);
      } finally {
        this.loading = false;
        this.showLoading(false);
        /* Same re-check pattern for both directions */
        requestAnimationFrame(() => {
          this.checkBottomSentinel();
          this.checkPendingSpacers();
        });
      }
    }

    trimIfNeeded(from) {
      while (this.pagesInDom.size > this.windowPages) {
        const sorted = Array.from(this.pagesInDom).sort((a, b) => a - b);
        const candidates = from === 'bottom' ? sorted.slice().reverse() : sorted.slice();
        /* Never trim a page that's in or near the viewport — otherwise its
           freshly-created spacer immediately re-enters the IO range and
           rehydrates, while the opposite end re-trims, causing a permanent
           ping-pong (every 2s: trim → rehydrate → trim → rehydrate …). */
        let trimmed = false;
        for (const page of candidates) {
          if (this.isPageNearViewport(page)) continue;
          this.trimPage(page);
          trimmed = true;
          break;
        }
        if (!trimmed) break; /* All in-DOM pages are near viewport; let DOM stay slightly over windowPages until user moves */
      }
    }

    isPageNearViewport(pageNum) {
      const items = this.grid.querySelectorAll('[data-virtual-page="' + pageNum + '"]');
      if (items.length === 0) return false;
      const first = items[0].getBoundingClientRect();
      const last = items[items.length - 1].getBoundingClientRect();
      /* Treat "near viewport" as within the same 800px buffer the IOs use —
         if a page is in this range, its trim+spacer would instantly re-fire. */
      return last.bottom > -800 && first.top < window.innerHeight + 800;
    }

    trimPage(pageNum) {
      const items = Array.from(
        this.grid.querySelectorAll('[data-virtual-page="' + pageNum + '"]')
      );
      if (items.length === 0) {
        this.pagesInDom.delete(pageNum);
        return;
      }

      /* Replace each trimmed product with a skeleton placeholder in the same
         grid slot. Grid auto-layout preserves the original visual height,
         so scroll position is naturally preserved. Skeletons animate with a
         shimmer (CSS) so users see "loading" instead of blank white when
         scrolling back through previously-loaded pages. */
      const skeletons = [];
      const anchor = items[0];
      const parent = anchor.parentNode;
      items.forEach((item) => {
        const skel = document.createElement('div');
        skel.className = 'grid-item infinite-scroll__skeleton';
        skel.dataset.virtualSkeletonPage = String(pageNum);
        parent.insertBefore(skel, item);
        skeletons.push(skel);
        item.remove();
      });

      this.pagesInDom.delete(pageNum);
      this.observeSkeletonGroup(skeletons, pageNum);
    }

    observeSkeletonGroup(skeletons, pageNum) {
      /* Observe every skeleton in the group — the group can be ~3000px tall
         and a single observer on just the first/last can miss the case where
         the user starts inside the group. */
      const obs = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (this.loading) return; /* keep observers connected for later */
          obs.disconnect();
          skeletons.forEach((s) => this.spacerObservers.delete(s));
          this.rehydratePage(skeletons[0], pageNum);
          return;
        }
      }, { rootMargin: '800px 0px 800px 0px' });
      skeletons.forEach((s) => {
        obs.observe(s);
        /* Track by every skeleton so checkPendingSpacers can sweep them. */
        this.spacerObservers.set(s, obs);
      });
    }

    /* Called from loadNext/rehydratePage finally — picks up any skeleton group
       that entered the viewport while a fetch was in flight (IO fired but we
       skipped because loading=true). Without this, skeletons would shimmer
       forever because IO won't refire on unchanged intersection state. */
    checkPendingSpacers() {
      if (this.loading) return;
      const entries = Array.from(this.spacerObservers.entries());
      const handled = new Set();
      for (const [skeleton, obs] of entries) {
        if (handled.has(obs)) continue; /* skip siblings already handled */
        if (!skeleton.isConnected) {
          this.spacerObservers.delete(skeleton);
          continue;
        }
        const rect = skeleton.getBoundingClientRect();
        if (rect.top < window.innerHeight + 800 && rect.bottom > -800) {
          handled.add(obs);
          obs.disconnect();
          const pageNum = parseInt(skeleton.dataset.virtualSkeletonPage, 10);
          /* Clear all tracking for this group */
          for (const [s, o] of entries) {
            if (o === obs) this.spacerObservers.delete(s);
          }
          this.rehydratePage(skeleton, pageNum);
          return; /* one at a time — next finally will pick up the rest */
        }
      }
    }

    pageUrl(pageNum) {
      const u = new URL(this.baseUrl, window.location.origin);
      if (pageNum > 1) {
        u.searchParams.set('page', String(pageNum));
      } else {
        u.searchParams.delete('page');
      }
      return u.pathname + (u.search || '');
    }

    updateUrl(pageNum) {
      try { history.replaceState({}, '', this.pageUrl(pageNum)); } catch (e) { /* noop */ }
      const currentEl = this.querySelector(SEL_CURRENT);
      if (currentEl) currentEl.textContent = String(pageNum);
    }

    setupScrollUrlSync() {
      let ticking = false;
      let lastReported = this.currentPage;
      this._scrollHandler = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const viewportCenter = window.scrollY + window.innerHeight / 2;
          let visiblePage = null;
          this.pagesInDom.forEach((pageNum) => {
            const items = this.grid.querySelectorAll('[data-virtual-page="' + pageNum + '"]');
            if (items.length === 0) return;
            const firstTop = items[0].getBoundingClientRect().top + window.scrollY;
            const lastBottom = items[items.length - 1].getBoundingClientRect().bottom + window.scrollY;
            if (viewportCenter >= firstTop && viewportCenter <= lastBottom) {
              visiblePage = pageNum;
            }
          });
          if (visiblePage !== null && visiblePage !== lastReported) {
            lastReported = visiblePage;
            this.currentPage = visiblePage;
            this.updateUrl(visiblePage);
          }
        });
      };
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    async fetchDoc(url) {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    showLoading(visible) {
      const el = this.querySelector(SEL_LOADING);
      if (el) el.hidden = !visible;
    }
  }

  class CollectionProductRowWindow extends HTMLElement {
    constructor() {
      super();
      this.windowRows = parseInt(this.dataset.windowRows, 10) || 3;
      this.rootMargin = parseInt(this.dataset.rootMargin, 10) || 800;
      this.rowsInDom = new Set();
      this.loading = false;
      this.rowObservers = new Map();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rows = Array.from(this.querySelectorAll('[data-cpr-row]'));

      this.rows.forEach((row) => {
        const carousel = row.querySelector('[data-cpr-carousel]');
        if (!carousel) return;

        if (carousel.dataset.cprState === 'initial') {
          this.rowsInDom.add(row);
          this.mountSwiper(carousel);
        }

        this.observeRow(row);
      });
    }

    disconnectedCallback() {
      this.rowObservers.forEach((observer) => observer.disconnect());
      this.rowObservers.clear();
      this.initialized = false;
    }

    swiperOptions(carousel) {
      const row = carousel.closest('[data-cpr-row]');
      return {
        slidesPerView: 1.4,
        slidesPerGroup: 1,
        spaceBetween: 7,
        speed: 400,
        watchOverflow: true,
        navigation: {
          nextEl: row?.querySelector('.collection-product-row__arrow--next'),
          prevEl: row?.querySelector('.collection-product-row__arrow--prev'),
        },
        breakpoints: {
          375: { slidesPerView: 2.3, spaceBetween: 7 },
          768: { slidesPerView: 3, spaceBetween: 14 },
          1024: { slidesPerView: 4, spaceBetween: 16 },
          1280: { slidesPerView: 5, spaceBetween: 16 },
        },
      };
    }

    mountSwiper(carousel) {
      if (!carousel || carousel.swiper || typeof Swiper === 'undefined') return;
      new Swiper(carousel, this.swiperOptions(carousel));
    }

    destroySwiper(carousel) {
      if (carousel?.swiper && !carousel.swiper.destroyed) {
        carousel.swiper.destroy(true, true);
      }
    }

    createSkeletonSlides() {
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < 5; index += 1) {
        const skeleton = document.createElement('div');
        skeleton.className = 'swiper-slide collection-product-row__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.innerHTML = `
          <span class="collection-product-row__skeleton-media"></span>
          <span class="collection-product-row__skeleton-line collection-product-row__skeleton-line--short"></span>
          <span class="collection-product-row__skeleton-line"></span>
        `;
        fragment.append(skeleton);
      }

      return fragment;
    }

    observeRow(row) {
      if (!row?.isConnected || row.hidden || this.rowObservers.has(row)) return;

      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (this.loading) return;

          observer.disconnect();
          this.rowObservers.delete(row);
          this.rehydrateRow(row);
          return;
        }
      }, { rootMargin: `${this.rootMargin}px 0px ${this.rootMargin}px 0px` });

      observer.observe(row);
      this.rowObservers.set(row, observer);
    }

    async rehydrateRow(row) {
      if (this.loading || !row?.isConnected) return;

      const carousel = row.querySelector('[data-cpr-carousel]');
      const wrapper = carousel?.querySelector('.swiper-wrapper');
      const source = carousel?.dataset.cprSource;
      if (!carousel || !wrapper || !source) return;

      const hadProductSlides = Boolean(
        wrapper.querySelector('.swiper-slide:not(.collection-product-row__skeleton)')
      );
      const rowHeightBefore = row.getBoundingClientRect().height;
      const rowTopBefore = row.getBoundingClientRect().top;
      const trimFrom = rowTopBefore < window.innerHeight / 2 ? 'bottom' : 'top';

      this.loading = true;
      carousel.dataset.cprState = 'loading';
      carousel.setAttribute('aria-busy', 'true');

      try {
        const doc = await this.fetchDoc(source);
        if (!this.isConnected || !row.isConnected) return;

        const slides = Array.from(doc.querySelectorAll('.swiper-slide'));
        if (!slides.length) {
          row.hidden = true;
          carousel.dataset.cprState = 'empty';
          this.rowsInDom.delete(row);
          return;
        }

        this.destroySwiper(carousel);
        wrapper.replaceChildren(...slides);
        row.classList.remove('is-deferred');
        carousel.dataset.cprState = 'loaded';
        carousel.setAttribute('aria-busy', 'false');
        this.rowsInDom.add(row);
        this.mountSwiper(carousel);
        this.trimIfNeeded(trimFrom);

        requestAnimationFrame(() => {
          carousel.style.removeProperty('height');
          if (rowTopBefore < 0) {
            const heightDelta = row.getBoundingClientRect().height - rowHeightBefore;
            if (Math.abs(heightDelta) > 1) window.scrollBy(0, heightDelta);
          }
        });
      } catch (error) {
        carousel.dataset.cprState = hadProductSlides ? 'loaded' : 'error';
        carousel.setAttribute('aria-busy', hadProductSlides ? 'false' : 'true');
        if (hadProductSlides) this.mountSwiper(carousel);
        console.error('[collection-product-row] rehydrateRow', error);
      } finally {
        this.loading = false;
        requestAnimationFrame(() => this.checkPendingRows());
      }
    }

    trimIfNeeded(from) {
      while (this.rowsInDom.size > this.windowRows) {
        const sorted = this.rows
          .filter((row) => this.rowsInDom.has(row))
          .sort((first, second) => this.rows.indexOf(first) - this.rows.indexOf(second));
        const candidates = from === 'bottom' ? sorted.slice().reverse() : sorted;
        let trimmed = false;

        for (const row of candidates) {
          if (this.isRowNearViewport(row)) continue;
          this.trimRow(row);
          trimmed = true;
          break;
        }

        if (!trimmed) break;
      }
    }

    isRowNearViewport(row) {
      const rect = row.getBoundingClientRect();
      return rect.bottom > -this.rootMargin && rect.top < window.innerHeight + this.rootMargin;
    }

    trimRow(row) {
      const carousel = row.querySelector('[data-cpr-carousel]');
      const wrapper = carousel?.querySelector('.swiper-wrapper');
      if (!carousel || !wrapper) {
        this.rowsInDom.delete(row);
        return;
      }

      const preservedHeight = carousel.getBoundingClientRect().height;
      this.destroySwiper(carousel);
      if (preservedHeight > 0) carousel.style.height = `${preservedHeight}px`;
      wrapper.replaceChildren(this.createSkeletonSlides());

      row.classList.add('is-deferred');
      carousel.dataset.cprState = 'idle';
      carousel.setAttribute('aria-busy', 'true');
      this.rowsInDom.delete(row);
      this.observeRow(row);
    }

    checkPendingRows() {
      if (this.loading) return;

      const entries = Array.from(this.rowObservers.entries());
      for (const [row, observer] of entries) {
        if (!row.isConnected) {
          observer.disconnect();
          this.rowObservers.delete(row);
          continue;
        }

        if (this.isRowNearViewport(row)) {
          observer.disconnect();
          this.rowObservers.delete(row);
          this.rehydrateRow(row);
          return;
        }
      }
    }

    async fetchDoc(url) {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }
  }

  if (!customElements.get('infinite-scroll')) {
    customElements.define('infinite-scroll', InfiniteScroll);
  }
  if (!customElements.get('collection-product-row-window')) {
    customElements.define('collection-product-row-window', CollectionProductRowWindow);
  }
}
